/**
 * PushNotificationService - FCM Push Notification Registration & Handling
 * Phase 24: Push notification plumbing for Android (Capacitor)
 * 
 * Responsibilities:
 * - Detect if running inside Capacitor native shell
 * - Request push notification permission on first launch
 * - Register for FCM and obtain device token
 * - Send device token to backend for storage
 * - Handle incoming notifications (foreground + background tap)
 * - Deep link back into the app on notification tap
 * 
 * This service is a no-op when running in a regular browser.
 * Push notifications only work inside the Capacitor native app.
 */

import { authFetch } from './authFetch.js';

const API_URL = import.meta.env?.VITE_BACKEND_URL || 'https://6wdv4xnpei.execute-api.us-west-1.amazonaws.com/prod';

class PushNotificationService {
    constructor() {
        this.deviceToken = null;
        this.isNative = false;
        this.initialized = false;
        this.listeners = [];
    }

    /**
     * Check if we're running inside a Capacitor native shell
     */
    isCapacitorAvailable() {
        return !!(window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform());
    }

    /**
     * Initialize push notifications.
     * Call this after the user is authenticated.
     * No-op in browser — only runs inside Capacitor.
     * 
     * @param {string} userId - The authenticated user's ID
     */
    async init(userId) {
        if (this.initialized) return;
        if (!this.isCapacitorAvailable()) {
            console.log('🔔 Push notifications: not in native app, skipping');
            return;
        }

        this.isNative = true;
        this.userId = userId;
        this.initialized = true;

        console.log('🔔 Push notifications: initializing in Capacitor');

        try {
            const PushNotifications = window.Capacitor.Plugins.PushNotifications;
            if (!PushNotifications) {
                console.warn('🔔 PushNotifications plugin not available');
                return;
            }

            // Check current permission status
            const permStatus = await PushNotifications.checkPermissions();
            console.log('🔔 Push permission status:', permStatus.receive);

            if (permStatus.receive === 'prompt' || permStatus.receive === 'prompt-with-rationale') {
                // Request permission — shows system dialog
                const result = await PushNotifications.requestPermissions();
                if (result.receive !== 'granted') {
                    console.log('🔔 Push permission denied by user');
                    return;
                }
            } else if (permStatus.receive !== 'granted') {
                console.log('🔔 Push permission not granted:', permStatus.receive);
                return;
            }

            // Register for push notifications (triggers FCM registration)
            await PushNotifications.register();

            // Listen for registration success — gives us the FCM token
            this._addListener(
                await PushNotifications.addListener('registration', async (token) => {
                    console.log('🔔 FCM token received:', token.value.substring(0, 20) + '...');
                    this.deviceToken = token.value;
                    await this._sendTokenToBackend(token.value);
                })
            );

            // Listen for registration errors
            this._addListener(
                await PushNotifications.addListener('registrationError', (error) => {
                    console.error('🔔 Push registration error:', error);
                })
            );

            // Listen for notifications received while app is in foreground
            this._addListener(
                await PushNotifications.addListener('pushNotificationReceived', (notification) => {
                    console.log('🔔 Notification received (foreground):', notification);
                    this._handleForegroundNotification(notification);
                })
            );

            // Listen for notification tap (app opened from notification)
            this._addListener(
                await PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
                    console.log('🔔 Notification tapped:', action);
                    this._handleNotificationTap(action.notification);
                })
            );

            console.log('🔔 Push notification listeners registered');

        } catch (error) {
            console.error('🔔 Push notification init error:', error);
        }
    }

    /**
     * Send the FCM device token to the backend for storage
     * @param {string} token - FCM device token
     */
    async _sendTokenToBackend(token) {
        if (!this.userId) {
            console.warn('🔔 Cannot send token — no userId');
            return;
        }

        try {
            const response = await authFetch(`${API_URL}/api/users/device-token`, {
                method: 'POST',
                body: JSON.stringify({
                    userId: this.userId,
                    deviceToken: token,
                    platform: 'android',
                    updatedAt: new Date().toISOString()
                })
            });

            if (response.ok) {
                console.log('🔔 Device token saved to backend');
            } else {
                const data = await response.json().catch(() => ({}));
                console.warn('🔔 Failed to save device token:', response.status, data);
            }
        } catch (error) {
            console.error('🔔 Error sending device token to backend:', error);
        }
    }

    /**
     * Handle a notification received while the app is in the foreground.
     * Capacitor config already shows system notification (badge, sound, alert).
     * We just update the in-app badge count here.
     */
    _handleForegroundNotification(notification) {
        const { title, body, data } = notification;
        console.log('🔔 Foreground notification:', { title, body, data });

        // Increment in-app notification badge if element exists
        this._incrementNotificationBadge();
    }

    /**
     * Handle notification tap — deep link to the relevant screen.
     * The notification data payload contains a deepLink field.
     */
    _handleNotificationTap(notification) {
        const data = notification.data || {};
        const deepLink = data.deepLink;

        if (deepLink) {
            console.log('🔔 Deep linking to:', deepLink);
            this._navigateToDeepLink(deepLink);
        }
    }

    /**
     * Navigate to a deep link path within the app.
     * Since this is a single-page app, we handle routing internally.
     */
    _navigateToDeepLink(path) {
        // Store for any component that needs to check on load
        window._pendingDeepLink = path;

        // Fuel stop report — open verification modal
        if (path.startsWith('/report/fuel/')) {
            const fuelStopId = path.replace('/report/fuel/', '');
            if (window.verifyFuelStop) {
                window.verifyFuelStop(fuelStopId, 'Fuel Stop');
            }
        }

        // Wind report — open road report modal
        if (path.startsWith('/report/wind/')) {
            if (window.openRoadReportModal) {
                window.openRoadReportModal();
            }
        }
    }

    /**
     * Increment the notification badge count in the UI.
     * Wires into the future in-app notification center (Phase 21).
     */
    _incrementNotificationBadge() {
        const badge = document.getElementById('notificationBadge');
        if (badge) {
            const current = parseInt(badge.textContent || '0', 10);
            badge.textContent = String(current + 1);
            badge.style.display = 'inline';
        }
    }

    /**
     * Track listeners for cleanup
     */
    _addListener(listener) {
        if (listener) this.listeners.push(listener);
    }

    /**
     * Remove all listeners (call on logout or app destroy)
     */
    async cleanup() {
        for (const listener of this.listeners) {
            if (listener && listener.remove) {
                await listener.remove();
            }
        }
        this.listeners = [];
        this.initialized = false;
        this.deviceToken = null;
        console.log('🔔 Push notification listeners cleaned up');
    }

    /**
     * Get the current device token (null if not registered)
     */
    getDeviceToken() {
        return this.deviceToken;
    }

    /**
     * Check if push notifications are supported and enabled
     */
    isEnabled() {
        return this.isNative && this.deviceToken !== null;
    }
}

// Export singleton
export const pushNotificationService = new PushNotificationService();
